import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Download, Trash2, Plus, Search, Sparkles, Image as ImageIcon, Film } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { getStoredToken } from '../App';

const API = process.env.REACT_APP_BACKEND_URL;

// Engines that produce IMAGE outputs (everything else is video).
const IMAGE_ENGINES = new Set(['gpt_image', 'seedream_sequential']);

const ENGINE_LABELS = {
  seedance: 'Seedance',
  wan: 'Wan 2.6',
  grok: 'Grok',
  vidu: 'Vidu Q3',
  kling: 'Kling 3.0',
  sora: 'Sora 2',
  gpt_image: 'GPT Image',
  seedance_spicy: 'Seedance Spicy',
  wan_spicy: 'Wan Spicy',
  seedream_sequential: 'Char Sheets',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

const truncate = (s, n) => (s && s.length > n ? `${s.slice(0, n).trim()}…` : (s || ''));

/**
 * AIClipsLibrary
 * --------------
 * Browse, reuse, download and delete past AI generations.
 *
 * Props:
 *   onUseClip(clip)  — optional. When provided, a "Use" button appears that
 *                      hands the selected clip back to the parent (e.g. to drop
 *                      into a slideshow or video timeline). Clip shape:
 *                        { url, type: 'video'|'photo', duration, engine }
 *   compact          — bool. true = denser layout for embedding in modals.
 *   typeFilter       — 'video' | 'image' | undefined (=all). Optional default
 *                      filter that hides the type pill when set.
 */
export const AIClipsLibrary = ({ onUseClip, compact = false, typeFilter }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState(typeFilter || 'all'); // 'all' | 'video' | 'image'

  const fetchClips = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getStoredToken();
      const params = new URLSearchParams({ limit: '100' });
      if (typeFilter === 'video' || typeFilter === 'image') {
        params.set('media_type', typeFilter);
      } else if (activeType !== 'all') {
        params.set('media_type', activeType);
      }
      const r = await fetch(`${API}/api/ai-generations?${params.toString()}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Failed to load (${r.status})`);
      const d = await r.json();
      setItems(d.items || []);
    } catch (e) {
      setError(e.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [activeType, typeFilter]);

  useEffect(() => { fetchClips(); }, [fetchClips]);

  const handleDelete = async (jobId) => {
    if (!window.confirm('Remove this clip from your library? The file stays on the server in case other projects use it.')) return;
    try {
      const token = getStoredToken();
      const r = await fetch(`${API}/api/ai-generations/${jobId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error(`Delete failed (${r.status})`);
      setItems(prev => prev.filter(c => c.job_id !== jobId));
      toast.success('Clip removed from library');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const handleDownload = async (clip) => {
    if (!clip.url) return;
    try {
      const response = await fetch(clip.url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const isImage = IMAGE_ENGINES.has(clip.engine);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${clip.custom_name || `ai_${clip.engine}_${clip.job_id}`}.${isImage ? 'png' : 'mp4'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(clip.url, '_blank');
    }
  };

  const handleUse = (clip) => {
    if (!onUseClip) return;
    const isImage = IMAGE_ENGINES.has(clip.engine);
    onUseClip({
      url: clip.url,
      type: isImage ? 'photo' : 'video',
      duration: isImage ? undefined : (clip.duration || undefined),
      engine: ENGINE_LABELS[clip.engine] || clip.engine,
    });
  };

  // Filter items by free-text search on prompt/custom_name
  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (c.prompt || '').toLowerCase().includes(q) ||
      (c.custom_name || '').toLowerCase().includes(q) ||
      (ENGINE_LABELS[c.engine] || '').toLowerCase().includes(q)
    );
  });

  const showTypeTabs = !typeFilter;

  return (
    <div className="flex flex-col gap-4" data-testid="ai-clips-library">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prompts, names, engines…"
            className="pl-9 bg-zinc-900 border-zinc-700"
            data-testid="ai-clips-search-input"
          />
        </div>
        {showTypeTabs && (
          <div className="flex gap-1 p-1 rounded-lg bg-zinc-900 border border-zinc-800 self-start">
            {[
              { id: 'all', label: 'All', icon: Sparkles },
              { id: 'video', label: 'Videos', icon: Film },
              { id: 'image', label: 'Images', icon: ImageIcon },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveType(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  activeType === id
                    ? 'bg-zinc-700 text-white'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
                data-testid={`ai-clips-filter-${id}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Body */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mb-3" />
          <p className="text-sm">Loading your AI clips…</p>
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-16 text-red-400">
          <p className="text-sm">{error}</p>
          <Button onClick={fetchClips} variant="outline" size="sm" className="mt-3">Retry</Button>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-16 text-zinc-500" data-testid="ai-clips-empty-state">
          <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium text-zinc-400">No AI clips yet</p>
          <p className="text-xs mt-1">
            {search.trim() ? 'Try a different search.' : 'Generate something and it\'ll show up here automatically.'}
          </p>
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div className={`grid gap-4 ${compact ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'}`}>
          {filtered.map((clip) => {
            const isImage = IMAGE_ENGINES.has(clip.engine);
            return (
              <div
                key={clip.job_id}
                className="group relative bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden hover:border-zinc-600 transition-colors"
                data-testid={`ai-clip-card-${clip.job_id}`}
              >
                {/* Thumbnail — uses object-contain so the full frame is
                    visible (no cropping). The image is what users rely on
                    to identify which clip is which, so we can't lose edges. */}
                <div className="relative aspect-video bg-black flex items-center justify-center">
                  {isImage ? (
                    <img
                      src={clip.url}
                      alt={truncate(clip.prompt, 40)}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <video
                      src={`${clip.url}#t=0.1`}
                      preload="metadata"
                      muted
                      playsInline
                      className="max-w-full max-h-full object-contain"
                      onMouseEnter={(e) => { e.currentTarget.play().catch(() => {}); }}
                      onMouseLeave={(e) => { e.currentTarget.pause(); try { e.currentTarget.currentTime = 0.1; } catch { /* ignore */ } }}
                    />
                  )}
                  {/* Engine badge */}
                  <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium rounded-full bg-black/70 text-white backdrop-blur-sm">
                    {ENGINE_LABELS[clip.engine] || clip.engine}
                  </span>
                  {!isImage && clip.duration && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 text-[10px] font-mono rounded bg-black/70 text-white backdrop-blur-sm">
                      {clip.duration}s
                    </span>
                  )}
                </div>

                {/* Meta */}
                <div className="p-3">
                  <p className="text-xs text-zinc-300 line-clamp-2 min-h-[2.5rem]" title={clip.prompt}>
                    {clip.custom_name || clip.prompt || 'Untitled'}
                  </p>
                  <p className="text-[10px] text-zinc-500 mt-1">{formatDate(clip.completed_at || clip.created_at)}</p>

                  {/* Actions */}
                  <div className="flex gap-1.5 mt-2.5">
                    {onUseClip && (
                      <Button
                        size="sm"
                        onClick={() => handleUse(clip)}
                        className="flex-1 h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`ai-clip-use-${clip.job_id}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Use
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(clip)}
                      className="h-8 px-2 text-xs"
                      title="Download"
                      data-testid={`ai-clip-download-${clip.job_id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(clip.job_id)}
                      className="h-8 px-2 text-xs hover:text-red-400 hover:border-red-500/50"
                      title="Remove from library"
                      data-testid={`ai-clip-delete-${clip.job_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AIClipsLibrary;
